import { useState, useMemo, useEffect, useCallback } from "react";
import { PlusCircle, UserCog, Trash2, X } from "lucide-react";
import { developerAccessService, type IssuedTokenRow, ENVIRONMENTS } from "./developerAccessService";
import { mcpResourcesService, type MCPResourceRow } from "../MCPResources/mcpResourcesService";

const GRADIENTS = [
  "from-violet-600 to-indigo-500",
  "from-orange-600 to-yellow-500",
  "from-teal-600 to-blue-500",
  "from-purple-600 to-pink-500",
  "from-orange-500 to-red-500",
  "from-teal-500 to-emerald-400",
  "from-zinc-600 to-zinc-400",
];

function initFromName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export type Dev = {
  id: string;
  name: string;
  email: string;
  init: string;
  grad: string;
  role: string;
  roleLabel: string;
  env: string;
  roleDesc: string;
  defaultRes: string[];
};

type Env    = "production" | "staging" | "dev";
type Expiry = "7d" | "30d" | "90d" | "180d" | "unlimited";

// ── DeveloperAccess ─────────────────────────────────────────────────
export default function MCPDevelopers() {
  const [showForm, setShowForm] = useState(false);

  // form state
  const [search, setSearch]       = useState("");
  const [ddOpen, setDdOpen]       = useState(false);
  const [dev, setDev]             = useState<Dev | null>(null);
  const [expiry, setExpiry]       = useState<Expiry>("30d");
  const [environment, setEnvironment] = useState<string>("production");
  const [selRes, setSelRes]       = useState<Set<string>>(new Set());
  const [resFilter, setResFilter] = useState("");
  const [notes, setNotes]         = useState("");
  const [rateLimit, setRateLimit] = useState("200");
  const [label, setLabel]         = useState("");

  // success / copy
  const [token, setToken]     = useState("");
  const [success, setSuccess] = useState(false);
  const [copied, setCopied]   = useState(false);

  // data from API
  const [devs, setDevs] = useState<Dev[]>([]);
  const [roles, setRoles] = useState<{ roleId: string; roleName: string }[]>([]);
  const [resources, setResources] = useState<MCPResourceRow[]>([]);
  const [issuedTokens, setIssuedTokens] = useState<IssuedTokenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // role for the token (from selected developer, not editable)
  const [selectedRoleForToken, setSelectedRoleForToken] = useState<string>("");

  // confirm toggle (revoke) before applying
  const [confirmToggle, setConfirmToggle] = useState<{ id: string; newValue: boolean } | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  // confirm delete (permanent remove)
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; dev: string } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [users, rolesList, resList, tokens] = await Promise.all([
        developerAccessService.getUsers(),
        developerAccessService.getRoles(),
        mcpResourcesService.getAll(),
        developerAccessService.getIssuedTokens(),
      ]);
      setRoles(rolesList);
      setDevs(users.map((u, i) => ({
        id: u.id,
        name: [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email,
        email: u.email,
        init: initFromName([u.firstName, u.lastName].filter(Boolean).join(" ") || u.email),
        grad: GRADIENTS[i % GRADIENTS.length],
        role: u.roleName || "user",
        roleLabel: u.roleName || "User",
        env: "production",
        roleDesc: "",
        defaultRes: [],
      })));
      setResources(resList.filter((r: MCPResourceRow) => r.isActive));
      setIssuedTokens(tokens);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load data");
      setIssuedTokens([]);
      setDevs([]);
      setResources([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredDevs = useMemo(() =>
    devs.filter(d => d.name.toLowerCase().includes(search.toLowerCase()) || d.email.includes(search)),
  [devs, search]);

  const filteredRes = useMemo(() =>
    resources.filter(r =>
      (r.id + r.resourceId + r.name + r.cat).toLowerCase().includes(resFilter.toLowerCase())
    ),
  [resources, resFilter]);

  const chooseDev = (d: Dev) => {
    setDev(d);
    setSelRes(new Set(d.defaultRes));
    const userRole = d.roleLabel || d.role || "User";
    const roleFromList = roles.some((r) => r.roleName === userRole) ? userRole : (roles[0]?.roleName ?? userRole);
    setSelectedRoleForToken(roleFromList);
    setDdOpen(false);
    setSearch("");
  };
  const clearDev = () => { setDev(null); setSelRes(new Set()); setSelectedRoleForToken(""); };
  const toggleRes = (id: string) => setSelRes(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const generate = async () => {
    if (!dev || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await developerAccessService.issueToken({
        developerUserId: dev.id,
        roleName: selectedRoleForToken || undefined,
        environment,
        selectedMCPResourceIds: Array.from(selRes),
        expiry,
        label: label || undefined,
        notes: notes || undefined,
        rateLimitPerMinute: rateLimit ? parseInt(rateLimit, 10) : undefined,
      });
      setToken(res.token);
      setIssuedTokens(prev => [{
        id: res.id,
        dev: res.developerName,
        role: res.roleName ?? "",
        env: environment,
        token: res.token.slice(0, 12) + "…",
        issued: res.issuedAt.slice(0, 10),
        isActive: true,
        init: initFromName(res.developerName),
        grad: GRADIENTS[0],
      }, ...prev]);
      setSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to issue token");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSuccess(false); setToken(""); setDev(null); setSelRes(new Set());
    setSelectedRoleForToken(""); setEnvironment("production"); setNotes(""); setLabel(""); setShowForm(false);
  };

  const copy = () => {
    navigator.clipboard.writeText(token).catch(() => {});
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const expiryDate = () => {
    if (expiry === "unlimited") return "Never";
    const days = parseInt(expiry, 10);
    if (Number.isNaN(days)) return "—";
    const d = new Date(); d.setDate(d.getDate() + days);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const applyRevoke = async () => {
    if (!confirmToggle) return;
    try {
      await developerAccessService.revokeToken(confirmToggle.id);
      await fetchData();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to revoke token");
    }
    setConfirmToggle(null);
  };

  const applyReactivate = async (id: string) => {
    if (togglingId) return;
    setTogglingId(id);
    setError(null);
    try {
      await developerAccessService.reactivateToken(id);
      await fetchData();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to reactivate token");
    } finally {
      setTogglingId(null);
    }
  };

  const applyDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await developerAccessService.deleteToken(deleteConfirm.id);
      await fetchData();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete token");
    }
    setDeleteConfirm(null);
  };

  return (
    <div className="p-6 space-y-6 text-sm text-gray-300">

      {error && (
        <div className="rounded border border-red-800 bg-red-900/30 px-4 py-2 text-xs text-red-200">{error}</div>
      )}

      {/* ── header ──────────────────────────────────────────────── */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-white">Developer Access</h2>
          <p className="text-sm text-gray-400">Issue and manage MCP access tokens for developers.</p>
        </div>
        <button
          onClick={() => { setShowForm(true); setSuccess(false); setToken(""); }}
          className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm shadow transition-colors"
        >
          <PlusCircle className="w-4 h-4 mr-2" />
          Issue New Token
        </button>
      </div>

      {/* ══ ISSUE TOKEN POPUP ══════════════════════════════════════ */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div
            className="bg-neutral-900 border border-neutral-800 rounded-lg shadow-xl w-full max-w-[50vw] max-h-[90vh] overflow-hidden flex flex-col"
          >
            <div className="flex items-center justify-between px-4 py-3 bg-neutral-900 border-b border-neutral-800 flex-shrink-0">
              <div>
                <h3 className="font-semibold text-white text-sm">
                  {success ? "Token Generated" : "Issue New Token"}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {success ? "Share securely — shown once only." : "Select a developer → choose resources → generate MCP token"}
                </p>
              </div>
              <button
                type="button"
                onClick={resetForm}
                className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-neutral-800 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {!success ? (
          <div className="p-5 overflow-y-auto flex-1" style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 20, alignItems: "start" }}>

            {/* LEFT */}
            <div className="space-y-5">

              {/* Developer select */}
              <div>
                <p className="text-xs text-gray-400 font-medium mb-2">Select Developer</p>
                {!dev ? (
                  <div className="relative">
                    <input className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-1.5 text-xs text-gray-200 placeholder:text-gray-600 outline-none focus:border-indigo-500 transition-colors"
                      placeholder="🔍  Search by name or email…"
                      value={search} onChange={e => setSearch(e.target.value)}
                      onFocus={() => setDdOpen(true)}
                      onBlur={() => setTimeout(() => setDdOpen(false), 160)}
                    />
                    {ddOpen && (
                      <div className="absolute z-50 top-[calc(100%+4px)] left-0 right-0 bg-neutral-800 border border-neutral-700 rounded overflow-hidden shadow-2xl shadow-black/60">
                        {filteredDevs.map(d => (
                          <div key={d.id} onMouseDown={() => chooseDev(d)}
                            className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-neutral-700 transition-colors border-b border-neutral-700 last:border-0">
                            <div className={`w-7 h-7 rounded bg-gradient-to-br ${d.grad} flex items-center justify-center text-xs font-bold flex-shrink-0`}>{d.init}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-200">{d.name}</p>
                              <p className="font-mono text-[10px] text-gray-500">{d.email}</p>
                            </div>
                            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-900/50 text-indigo-300 border border-indigo-700/30 flex-shrink-0">
                              {d.roleLabel}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-start gap-3 bg-neutral-800 border border-neutral-700 rounded p-3">
                    <div className={`w-9 h-9 rounded bg-gradient-to-br ${dev.grad} flex items-center justify-center text-sm font-bold flex-shrink-0`}>{dev.init}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white">{dev.name}</p>
                      <p className="font-mono text-xs text-gray-500 mb-1.5">{dev.email}</p>
                      <div className="flex gap-1.5 flex-wrap">
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-900/50 text-indigo-300 border border-indigo-700/30">{dev.roleLabel}</span>
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-neutral-700 text-gray-300">{dev.env}</span>
                      </div>
                      <p className="text-[10px] text-gray-500 mt-1.5">{dev.roleDesc}</p>
                    </div>
                    <button onClick={clearDev} className="flex-shrink-0 text-xs text-gray-400 hover:text-white border border-neutral-600 rounded px-2 py-1 transition-colors">
                      Change
                    </button>
                  </div>
                )}
              </div>

              {/* Environment */}
              <div>
                <p className="text-xs text-gray-400 font-medium mb-2">Environment</p>
                <select
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value)}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-2 text-xs text-gray-200 outline-none focus:border-indigo-500 transition-colors"
                >
                  {ENVIRONMENTS.map((env) => (
                    <option key={env} value={env}>{env}</option>
                  ))}
                </select>
              </div>

              {/* Expiry */}
              <div>
                <p className="text-xs text-gray-400 font-medium mb-2">Token Expiry</p>
                <div className="grid grid-cols-4 gap-2">
                  {(["7d",   "90d", "180d", "unlimited"] as Expiry[]).map(ex => (
                    <button key={ex} onClick={() => setExpiry(ex)}
                      className={`py-2 rounded border text-center text-xs font-medium transition-colors ${
                        expiry === ex ? "border-indigo-600 bg-indigo-900/30 text-indigo-300" : "border-neutral-700 bg-neutral-800 text-gray-500 hover:border-neutral-600"
                      }`}
                    >
                      <span className="font-mono text-sm font-bold block mb-0.5">{ex.replace("d", "")}</span>
                      days
                    </button>
                  ))}
                </div>
              </div>

              {/* Resources */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-400 font-medium">Select MCP Resources</p>
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-indigo-900/50 text-indigo-300 border border-indigo-700/30">
                    {selRes.size} selected
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  {dev ? `Showing ${dev.roleLabel} defaults — customize below` : "Select a developer first"}
                </p>
                <div className="flex gap-2 mb-2">
                  <button onClick={() => setSelRes(new Set(resources.map(r => r.id)))} className="text-xs text-gray-400 border border-neutral-700 rounded px-2 py-1 hover:bg-neutral-800 transition-colors">Select All</button>
                  <button onClick={() => setSelRes(new Set())} className="text-xs text-gray-400 border border-neutral-700 rounded px-2 py-1 hover:bg-neutral-800 transition-colors">Clear All</button>
                  <input className="flex-1 bg-neutral-800 border border-neutral-700 rounded px-2 py-1 font-mono text-xs text-gray-300 placeholder:text-gray-600 outline-none focus:border-indigo-500 transition-colors"
                    placeholder="Filter resources…" value={resFilter} onChange={e => setResFilter(e.target.value)} />
                </div>
                <div className="space-y-1 max-h-64 overflow-y-auto pr-0.5">
                  {filteredRes.map(r => {
                    const sel = selRes.has(r.id);
                    return (
                      <div key={r.id} onClick={() => toggleRes(r.id)}
                        className={`flex items-center gap-2.5 p-2 rounded border cursor-pointer transition-colors ${
                          sel ? "border-indigo-700/40 bg-indigo-900/20" : "border-neutral-800 hover:bg-neutral-800/40"
                        }`}>
                        <div className={`w-4 h-4 rounded flex items-center justify-center text-[9px] flex-shrink-0 border ${
                          sel ? "bg-indigo-600 border-indigo-500 text-white font-bold" : "border-neutral-600"
                        }`}>
                          {sel && "✓"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-mono text-xs ${sel ? "text-indigo-400" : "text-gray-400"}`}>{r.resourceId}</p>
                          <p className="text-[10px] text-gray-500">{r.name}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Notes */}
              <div>
                <p className="text-xs text-gray-400 font-medium mb-2">Notes & Rate Limit</p>
                <textarea className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-2 text-xs text-gray-200 placeholder:text-gray-600 outline-none focus:border-indigo-500 resize-none transition-colors mb-3"
                  rows={2} placeholder="Purpose / notes for the audit trail…" value={notes} onChange={e => setNotes(e.target.value)} />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Rate Limit (req/min)</label>
                    <input type="number" className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-1.5 font-mono text-xs text-gray-200 outline-none focus:border-indigo-500 transition-colors"
                      value={rateLimit} onChange={e => setRateLimit(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Token Label</label>
                    <input className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-1.5 text-xs text-gray-200 placeholder:text-gray-600 outline-none focus:border-indigo-500 transition-colors"
                      placeholder="Auto-generated if blank" value={label} onChange={e => setLabel(e.target.value)} />
                  </div>
                </div>
              </div>

              {/* generate button */}
              <button onClick={generate} disabled={!dev || submitting}
                className={`flex items-center justify-center w-full py-2.5 rounded text-sm font-medium shadow transition-colors ${
                  dev && !submitting ? "bg-indigo-600 hover:bg-indigo-700 text-white" : "bg-neutral-800 text-neutral-600 cursor-not-allowed"
                }`}
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                {submitting ? "Issuing…" : "⚡ Generate MCP Token"}
              </button>
              <p className="text-xs text-gray-600 text-center -mt-3">Token will be shown once. Store it securely.</p>
            </div>

            {/* RIGHT: live preview */}
            <div className="border border-neutral-700 rounded overflow-hidden">
              <div className="px-4 py-3 bg-neutral-800 border-b border-neutral-700">
                <p className="text-sm font-semibold text-white">Token Preview</p>
                <p className="text-xs text-gray-400 mt-0.5">Live preview of access being granted</p>
              </div>
              <div className="p-4 bg-neutral-900/60">
                {!dev ? (
                  <div className="text-center py-8">
                    <div className="text-3xl opacity-20 mb-2">◎</div>
                    <p className="text-xs text-gray-600">Select a developer to<br />preview the token configuration</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 pb-3 border-b border-neutral-800">
                      <div className={`w-8 h-8 rounded bg-gradient-to-br ${dev.grad} flex items-center justify-center text-xs font-bold flex-shrink-0`}>{dev.init}</div>
                      <div>
                        <p className="text-xs font-semibold text-white">{dev.name}</p>
                        <p className="font-mono text-[10px] text-gray-500">{dev.role}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {[
                        { k: "Expiry",      v: `${expiry} · ${expiryDate()}` },
                        { k: "Rate Limit",  v: `${rateLimit} req/min` },
                        { k: "Resources",   v: `${selRes.size} selected` },
                      ].map((row, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-gray-500">{row.k}</span>
                          <span className="text-gray-200 font-medium">{row.v}</span>
                        </div>
                      ))}
                    </div>
                    {selRes.size > 0 && (
                      <div className="pt-2 border-t border-neutral-800">
                        <p className="text-[10px] text-gray-500 mb-1.5">Granted Scopes</p>
                        <div className="flex flex-wrap gap-1">
                          {/* {[...selRes].slice(0, 5).map(r => (
                            <span key={r} className="inline-block px-1.5 py-0.5 rounded text-[9px] font-mono font-medium bg-green-900/40 text-green-400 border border-green-700/30">{r}</span>
                          ))}
                          {selRes.size > 5 && (
                            <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-mono text-gray-500 border border-neutral-700">+{selRes.size - 5} more</span>
                          )} */}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
            ) : (
          <div className="p-5 overflow-y-auto flex-1 space-y-4">
            {/* banner */}
            <div className="bg-green-900/20 border border-green-700/30 rounded p-4 text-center">
              <div className="text-2xl mb-2">✓</div>
              <p className="text-sm font-semibold text-green-300">MCP Token Generated</p>
              <p className="text-xs text-gray-400 mt-1">Token is active and bound to developer identity. Share securely — shown once only.</p>
            </div>
            {/* token reveal */}
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 font-mono uppercase tracking-wider">MCP Token — copy now, never shown again</label>
              <div className="bg-neutral-800 border border-neutral-700 rounded p-3 font-mono text-xs text-green-400 break-all leading-relaxed mb-2">
                {token}
              </div>
              <div className="flex gap-2">
                <button onClick={copy} className="flex-1 bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-gray-300 rounded px-3 py-1.5 text-xs font-mono transition-colors">
                  {copied ? "✓ Copied!" : "⎘ Copy Token"}
                </button>
                <button onClick={resetForm} className="flex-1 bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-gray-300 rounded px-3 py-1.5 text-xs font-mono transition-colors">
                  + Issue Another
                </button>
              </div>
            </div>
            {/* config snippet */}
            <div>
              <label className="block text-xs text-gray-400 font-semibold mb-1.5">Developer Configuration</label>
              <pre className="bg-neutral-800 border border-neutral-700 rounded p-3 font-mono text-[10px] leading-relaxed text-gray-400 overflow-x-auto whitespace-pre">
{`// ~/.cursor/mcp.json
{
  "mcpServers": {
    "selficai": {
      "command": "npx",
      "args": ["-y", "@selficai/mcp-server"],
      "env": {
        "SELFIC_MCP_TOKEN": "${token}",
        "SELFIC_TENANT":    "tenant_acme_7f2a"
      }
    }
  }
}`}
              </pre>
            </div>
            <div className="bg-indigo-900/20 border border-indigo-700/30 rounded px-3 py-2 text-xs text-indigo-300">
              📋 This reveal has been logged to the audit trail with timestamp, admin identity, and developer assignment.
            </div>
          </div>
            )}
          </div>
        </div>
      )}

      {/* ── issued tokens table ──────────────────────────────────── */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <div>
            <h3 className="text-sm font-semibold text-white">Issued Tokens</h3>
            <p className="text-xs text-gray-400">All active and expired developer access tokens.</p>
          </div>
        </div>
        <div className="overflow-x-auto border border-neutral-800 rounded">
          {loading ? (
            <div className="px-4 py-8 text-center text-gray-400 text-sm">Loading…</div>
          ) : (
          <table className="min-w-full text-sm text-left text-gray-300">
            <thead className="bg-neutral-900 text-gray-400">
              <tr>
                {["Developer", "Role", "Environment", "Token", "Issued On", "Is Active", "Actions"].map(h => (
                  <th key={h} className="px-4 py-2 text-xs font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {issuedTokens.map((t) => (
                <tr key={t.id} className="border-t border-neutral-800 hover:bg-neutral-800/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded bg-gradient-to-br ${t.grad} flex items-center justify-center text-[9px] font-bold flex-shrink-0`}>{t.init}</div>
                      <span className="text-gray-200">{t.dev}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-indigo-900/50 text-indigo-300 border border-indigo-700/30">
                      {t.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400">{t.env}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{t.token}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">
                    {new Date(t.issued).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      disabled={togglingId === t.id}
                      onClick={() =>
                        t.isActive
                          ? setConfirmToggle({ id: t.id, newValue: false })
                          : applyReactivate(t.id)
                      }
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-neutral-900 disabled:opacity-60 disabled:cursor-wait ${t.isActive ? "bg-green-600" : "bg-neutral-600"}`}
                      role="switch"
                      aria-checked={t.isActive}
                      title={t.isActive ? "Revoke token" : "Reactivate token"}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${t.isActive ? "translate-x-5" : "translate-x-0.5"}`}
                        style={{ marginTop: 2 }}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button className="text-indigo-400 hover:text-indigo-300 transition-colors" title="Manage Permissions">
                        <UserCog className="w-4 h-4" />
                      </button>
                      <button className="text-red-400 hover:text-red-200 transition-colors" title="Delete token"
                        onClick={() => setDeleteConfirm({ id: t.id, dev: t.dev })}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      </div>

      {/* Confirm Revoke (toggle off = deactivate only) */}
      {confirmToggle !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
          <div className="bg-neutral-900 border border-neutral-700 rounded-lg shadow-xl max-w-sm w-full p-5">
            <h4 className="text-sm font-semibold text-white mb-1">Revoke token</h4>
            <p className="text-xs text-gray-400 mb-4">
              This token will be deactivated and cannot be used for MCP access. You can reactivate it later with the toggle.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConfirmToggle(null)}
                className="px-3 py-1.5 rounded text-xs font-medium border border-neutral-600 bg-neutral-800 text-gray-300 hover:bg-neutral-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyRevoke}
                className="px-3 py-1.5 rounded text-xs font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Revoke
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete (permanent remove) */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
          <div className="bg-neutral-900 border border-neutral-700 rounded-lg shadow-xl max-w-sm w-full p-5">
            <h4 className="text-sm font-semibold text-white mb-1">Delete token</h4>
            <p className="text-xs text-gray-400 mb-4">
              Permanently delete the token for <span className="text-gray-200 font-medium">&quot;{deleteConfirm.dev}&quot;</span>? This cannot be undone. The token will be removed from the list.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="px-3 py-1.5 rounded text-xs font-medium border border-neutral-600 bg-neutral-800 text-gray-300 hover:bg-neutral-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyDelete}
                className="px-3 py-1.5 rounded text-xs font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}